import React from 'react';
import Hero from '../components/Hero';
import DoctorGuidedGLP from '../components/DoctorGuidedGLP';
import SocialProof from '../components/SocialProof';
import StatsBlock from '../components/StatsBlock';
import HomeTeaser from '../components/HomeTeaser';
import Packages from '../components/Packages';
import WeightCalculator from '../components/WeightCalculator';
import Testimonials from '../components/Testimonials';
import FAQ from '../components/FAQ';
import ReferralBanner from '../components/ReferralBanner';
import CTASection from '../components/CTASection';

import Marquee from '../components/Marquee';
import Reveal from '../components/Reveal';
import StickyStory from '../components/StickyStory';
import ScanRing from '../components/ScanRing';

import { Stethoscope, ShieldCheck, Brain, Lock, Users, HeartPulse } from 'lucide-react';
import { BRAND_NAME, BRAND_POSITIONING } from '../utils/brand';

// Reassurance ticker — replaces traditional "trust badges row". Slowly scrolls
// past, paused on hover, frozen entirely under prefers-reduced-motion.
const MARQUEE_ITEMS = [
  <span key="a" className="inline-flex items-center gap-2"><Stethoscope size={14} className="text-brand-600" /> Licensed Healthcare Provider Network</span>,
  <span key="b" className="inline-flex items-center gap-2"><Brain size={14} className="text-brand-600" /> AI-Assisted Health Assessment</span>,
  <span key="c" className="inline-flex items-center gap-2"><ShieldCheck size={14} className="text-brand-600" /> Evidence-Based Medical Weight Management</span>,
  <span key="d" className="inline-flex items-center gap-2"><Lock size={14} className="text-brand-600" /> Secure &amp; PDPA-Conscious Platform</span>,
  <span key="e" className="inline-flex items-center gap-2"><Users size={14} className="text-brand-600" /> Personalised for Malaysians</span>,
  <span key="f" className="inline-flex items-center gap-2"><HeartPulse size={14} className="text-brand-600" /> Doctor-led care · AI-assisted support</span>,
];

// Sticky-story panels (Neko-style) — three scrolling thoughts paired with one
// pinned visual on the right.
const STORY_PANELS = [
  {
    eyebrow: 'Why MyDr.Ai',
    title: 'Healthcare should feel calm, not clinical.',
    body: `${BRAND_NAME} is built around licensed doctors and AI-assisted support — designed so the first step feels private, the consultation feels human, and the journey feels organised.`,
  },
  {
    eyebrow: 'Personalised pathway',
    title: 'No generic diet. No one-size-fits-all.',
    body: 'Your weight history, lifestyle, medical context and goals are reviewed by a licensed healthcare provider before any plan is suggested — and adjusted as you progress.',
  },
  {
    eyebrow: 'Support beyond the clinic',
    title: 'AI keeps the journey on track.',
    body: 'Reminders, education, follow-up prompts and progress check-ins live inside one calm dashboard — between you and the doctor reviewing your care.',
  },
];

// Pinned visual on the right of the sticky story — illustrative dashboard mock.
// Uses the brand teal/navy palette. No real patient data.
function StoryVisual() {
  return (
    <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-ink-200 bg-gradient-to-br from-brand-50 to-canvas shadow-card">
      <ScanRing className="absolute -top-1/4 -right-1/4 w-[36rem] h-[36rem]" tone="teal" />
      <div className="absolute inset-x-6 top-6 rounded-2xl bg-white p-5 shadow-soft">
        <div className="flex items-center gap-2 text-xs">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-brand-50 text-brand-700">
            <Stethoscope size={14} />
          </span>
          <div>
            <div className="font-semibold text-ink-900">Care plan ready</div>
            <div className="text-ink-500">Reviewed by your doctor</div>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg bg-ink-50 py-2">
            <div className="text-[10px] uppercase tracking-wide text-ink-500">Step</div>
            <div className="text-sm font-semibold text-ink-900">2 / 5</div>
          </div>
          <div className="rounded-lg bg-ink-50 py-2">
            <div className="text-[10px] uppercase tracking-wide text-ink-500">Next</div>
            <div className="text-sm font-semibold text-ink-900">Consult</div>
          </div>
          <div className="rounded-lg bg-ink-50 py-2">
            <div className="text-[10px] uppercase tracking-wide text-ink-500">Support</div>
            <div className="text-sm font-semibold text-ink-900">24/7</div>
          </div>
        </div>
      </div>
      <p className="absolute bottom-6 inset-x-6 text-[11px] text-ink-500 text-center">
        Illustrative example. Not real patient data.
      </p>
    </div>
  );
}

// Lede block — sits directly under the hero image. Single thought, calm cadence.
function Lede() {
  return (
    <section className="bg-canvas">
      <div className="container-x py-16 sm:py-20">
        <Reveal className="max-w-3xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold tracking-[0.18em] uppercase bg-brand-50 text-brand-700 border border-brand-100">
            {BRAND_POSITIONING}
          </span>
          <h2 className="mt-6 text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-ink-900">
            Lose weight smarter — with a doctor in the loop and AI by your side.
          </h2>
          <p className="mt-5 text-lg text-ink-600">
            {BRAND_NAME} is a healthcare technology platform for eligible adults
            in Malaysia. AI-assisted assessment, licensed doctor consultation,
            licensed pharmacy fulfilment, ongoing digital support — all in one
            calm experience.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <div id="hero" className="snap-screen flex flex-col">
        <Hero />
      </div>

      <div id="section-glp" className="snap-screen flex flex-col justify-center scroll-mt-16">
        <DoctorGuidedGLP />
      </div>

      <div id="section-steps" className="snap-screen flex flex-col justify-center scroll-mt-16">
        <Reveal><HomeTeaser /></Reveal>
      </div>

      <Reveal><SocialProof /></Reveal>
      <Reveal><StatsBlock /></Reveal>

      {/* <StickyStory panels={STORY_PANELS} visual={<StoryVisual />} /> */}

      {/* <Reveal><Packages /></Reveal> */}
      {/* <Reveal><WeightCalculator /></Reveal> */}
      <Reveal><Testimonials /></Reveal>
      {/* <Reveal><FAQ /></Reveal> */}

      {/* No vertical padding — the neighbouring Testimonials + CTASection
          already provide top/bottom breathing room; py-16 here was stacking. */}
      <section className="bg-white">
        <div className="container-x">
          <Reveal><ReferralBanner /></Reveal>
        </div>
      </section>

      <Reveal><CTASection /></Reveal>
    </>
  );
}
